import { TableColumnsType, Tag, Tooltip } from 'antd';
import { Batch } from '../../../shared/PreWorkServiceTypes';
import { useTranslation } from 'react-i18next';
import { CalendarOutlined } from '@ant-design/icons';
import { utc } from 'moment';
import { DATE_DD_MM_YYYY } from '../../../shared/Constants';
import ViewBatch from '../../../assets/images/view-batch.svg';
import EditBatch from '../../../assets/images/edit-batch.svg';
import EligibleBatch from '../../../assets/images/eligible-candidates.svg';
import ViewRegisteredCandidate from '../../../assets/images/view-registered-candidates.svg';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import useUserSession from '../../../hooks/useUserSession';
import { hasPrivilege } from '../../../shared/Utils';

export const BatchTableColumns = (
	handleEditClick: (batchId: number) => void,
	handleViewBatch: (batchId: number) => void,
	handleViewRegisteredCandidate: (batch: Batch) => void,
	handleEligibleCandidates: () => void
): TableColumnsType<Batch> => {
	const { t } = useTranslation();
	const { loggedInUserPrivileges } = useUserSession();

	const showEditBatch = hasPrivilege(
		PrivilegeCodes.UPDATE_BATCH,
		loggedInUserPrivileges
	);

	const showEligibleCandidates = hasPrivilege(
		PrivilegeCodes.GET_ELIGIBLE_CANDIDATES_FOR_NEXT_BATCH,
		loggedInUserPrivileges
	);

	const showViewRegisteredCandidates = hasPrivilege(
		PrivilegeCodes.GET_APPLICATIONS,
		loggedInUserPrivileges
	);

	return [
		{
			title: t('batchName'),
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: t('description'),
			dataIndex: 'description',
			key: 'description',
		},
		{
			title: t('registrationStartDate'),
			dataIndex: 'startDate',
			key: 'startDate',

			render: (__, record, index) => (
				<span key={index} className="flex items-center whitespace-nowrap">
					<CalendarOutlined />
					<span className="ml-2">
						{utc(record.startDate).format(DATE_DD_MM_YYYY)}
					</span>
				</span>
			),
		},
		{
			title: t('registrationEndDate'),
			dataIndex: 'endDate',
			key: 'endDate',
			render: (__, record, index) => (
				<span key={index} className="flex items-center whitespace-nowrap">
					<CalendarOutlined />
					<span className="ml-2">
						{utc(record.endDate).format(DATE_DD_MM_YYYY)}
					</span>
				</span>
			),
		},
		{
			title: t('status'),
			dataIndex: 'status',
			key: 'status',
			render: (__, record, index) => {
				if (record.status === 'ACTIVE') {
					return (
						<span key={index}>
							<Tag className="text-sm" color="success">
								{t('active')}
							</Tag>
						</span>
					);
				} else if (record.status === 'INACTIVE') {
					return (
						<span key={index}>
							<Tag className="text-sm" color="error">
								{t('inactive')}
							</Tag>
						</span>
					);
				} else if (record.status === 'IN_DRAFT') {
					return (
						<span key={index}>
							<Tag className="text-sm" color="warning">
								{t('draft')}
							</Tag>
						</span>
					);
				}
			},
		},
		{
			title: t('action'),
			key: 'operation',
			fixed: 'right',
			width: 300,
			render: (__, record) => (
				<div
					data-testid={record.status === 'ACTIVE' ? 'active-batch-actions' : ''}
					className="flex gap-6"
				>
					<Tooltip title={t('view')}>
						<div
							className="cursor-pointer"
							onClick={() => {
								void handleViewBatch(record.id as number);
							}}
						>
							<img
								src={ViewBatch}
								alt="view-batch"
								className="w-[1.4rem] h-[1.4rem] cursor-pointer"
							/>
						</div>
					</Tooltip>
					{showEditBatch && (
						<Tooltip title={t('editOrUpdate')}>
							<div
								className="cursor-pointer"
								onClick={() => {
									void handleEditClick(record.id as number);
								}}
							>
								<img
									src={EditBatch}
									alt="edit-batch"
									className="w-[1.4rem] h-[1.4rem]"
								/>
							</div>
						</Tooltip>
					)}
					{showEligibleCandidates && (
						<Tooltip title={t('eligibleCandidates')}>
							<div
								className="cursor-pointer"
								onClick={() => {
									void handleEligibleCandidates();
								}}
							>
								<img
									src={EligibleBatch}
									alt="eligible-candidates"
									className="w-[1.4rem] h-[1.4rem] cursor-pointer"
								/>
							</div>
						</Tooltip>
					)}
					{showViewRegisteredCandidates && (
						<Tooltip title={t('viewRegisteredCandidates')}>
							<div
								className="cursor-pointer"
								onClick={() => {
									void handleViewRegisteredCandidate(record);
								}}
							>
								<img
									src={ViewRegisteredCandidate}
									alt="view-registered-candidates"
									className="w-[1.5rem] h-[1.5rem] cursor-pointer"
								/>
							</div>
						</Tooltip>
					)}
				</div>
			),
		},
	];
};
